import { useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { fmtINRCompact } from "@/lib/format";

/**
 * Financial Position Hero · v2
 *
 * ONE cohesive visual — not three unrelated cards. Displays the net
 * position dominantly, with assets and liabilities as proportional
 * bars beneath. Privacy Mode instantly masks all values.
 *
 * Props:
 *   assets       — number (INR)
 *   liabilities  — number (INR)
 *   loading      — boolean
 */
function Mask() {
    return <span className="v2-mask v2-num">••••••</span>;
}

export default function FinancialPositionHero({ assets = 0, liabilities = 0, loading = false }) {
    const [hidden, setHidden] = useState(false);
    const net = (assets || 0) - (liabilities || 0);
    const total = Math.max(1, (assets || 0) + Math.abs(liabilities || 0));

    const { assetsPct, liabPct, netTone } = useMemo(() => {
        const a = Math.min(100, Math.max(2, ((assets || 0) / total) * 100));
        const l = Math.min(100, Math.max(2, ((Math.abs(liabilities || 0)) / total) * 100));
        let tone = "pos";
        if (net < 0) tone = "warn";
        return { assetsPct: a, liabPct: l, netTone: tone };
    }, [assets, liabilities, net, total]);

    return (
        <div className="v2-hero" data-testid="v2-position-hero">
            <div className="flex items-center justify-between mb-6">
                <span className="v2-eyebrow" style={{ color: "var(--v2-ink-inv-soft)" }}>
                    Financial Position
                </span>
                <button
                    type="button"
                    className="v2-btn v2-btn--ghost"
                    onClick={() => setHidden((h) => !h)}
                    aria-label={hidden ? "Reveal values" : "Hide values"}
                    data-testid="v2-position-privacy-toggle"
                    style={{ padding: "6px 12px", fontSize: 11 }}
                >
                    {hidden ? <Eye size={13} /> : <EyeOff size={13} />}
                    {hidden ? "Reveal" : "Privacy"}
                </button>
            </div>

            <div className="v2-position">
                <div className="v2-position__net">
                    <div className={`v2-position__amount v2-position__amount--${netTone}`} data-testid="v2-position-net">
                        {loading
                            ? <span className="v2-num">—</span>
                            : hidden
                                ? <Mask />
                                : <span className="v2-num">{fmtINRCompact(net)}</span>}
                    </div>
                    <div className="v2-position__label">Net Position</div>
                </div>

                <div className="v2-position__bars">
                    <div className="v2-position__row" data-testid="v2-position-assets">
                        <div className="v2-position__row-head">
                            <span className="v2-position__row-label">Assets</span>
                            <span className="v2-position__row-value">
                                {loading
                                    ? "—"
                                    : hidden
                                        ? <Mask />
                                        : fmtINRCompact(assets || 0)}
                            </span>
                        </div>
                        <div className="v2-position__bar">
                            <div
                                className="v2-position__bar-fill v2-position__bar-fill--assets"
                                style={{ width: `${assetsPct}%` }}
                            />
                        </div>
                    </div>

                    <div className="v2-position__row" data-testid="v2-position-liabilities">
                        <div className="v2-position__row-head">
                            <span className="v2-position__row-label">Liabilities</span>
                            <span className="v2-position__row-value">
                                {loading
                                    ? "—"
                                    : hidden
                                        ? <Mask />
                                        : fmtINRCompact(Math.abs(liabilities || 0))}
                            </span>
                        </div>
                        <div className="v2-position__bar">
                            <div
                                className="v2-position__bar-fill v2-position__bar-fill--liab"
                                style={{ width: `${liabPct}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
